<template>
  <!-- Free access redirect wrapper -->
  <div>
    <!-- ===== SUBSCRIPTION_FEATURE_START =====
    Payment success page temporarily disabled
    Users get free access without payment
    To re-enable: Change v-if="false" to v-if="true" below
    ===== -->
    <div v-if="false" class="payment-success-container"> <!-- Set to false to disable subscription payment flow -->
    <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-md w-full space-y-8">
        <div class="text-center">
          <!-- Success Icon -->
          <div v-if="paymentVerified" class="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-4">
            <svg class="h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>

          <!-- Loading Icon -->
          <div v-else-if="verifying" class="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-blue-100 mb-4">
            <i class="fa fa-spinner fa-spin text-3xl text-blue-600"></i>
          </div>

          <!-- Error Icon -->
          <div v-else-if="error" class="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-4">
            <svg class="h-12 w-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>

          <!-- Title -->
          <h2 class="mt-6 text-3xl font-extrabold text-gray-900">
            <span v-if="paymentVerified">Payment Successful!</span>
            <span v-else-if="verifying">Verifying Payment...</span>
            <span v-else-if="error">Payment Error</span>
          </h2>

          <!-- Message -->
          <p class="mt-2 text-sm text-gray-600">
            <span v-if="paymentVerified">
              Thank you for your subscription! Your account has been activated.
            </span>
            <span v-else-if="verifying">
              Please wait while we verify your payment...
            </span>
            <span v-else-if="error">
              {{ errorMessage }}
            </span>
          </p>

          <!-- Subscription Details -->
          <div v-if="paymentVerified && subscriptionDetails" class="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
            <div class="px-4 py-5 sm:px-6">
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Subscription Details
              </h3>
            </div>
            <div class="border-t border-gray-200">
              <dl>
                <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt class="text-sm font-medium text-gray-500">Plan</dt>
                  <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {{ subscriptionDetails.plan_type === 'yearly' ? 'Yearly' : 'Monthly' }} Subscription
                  </dd>
                </div>
                <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt class="text-sm font-medium text-gray-500">Status</dt>
                  <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {{ subscriptionDetails.status }}
                    </span>
                  </dd>
                </div>
                <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt class="text-sm font-medium text-gray-500">Next Billing Date</dt>
                  <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {{ formatDate(subscriptionDetails.current_period_end) }}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <!-- Actions -->
          <div class="mt-8 space-y-3">
            <button
              v-if="paymentVerified"
              @click="goToDashboard"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go to Dashboard
            </button>

            <button
              v-if="paymentVerified"
              @click="downloadInvoice"
              class="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              Download Invoice
            </button>

            <button
              v-if="error"
              @click="retryPayment"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Retry Payment
            </button>

            <a
              v-if="error"
              href="/dashboard"
              class="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Continue Without Payment
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
    <!-- ===== SUBSCRIPTION_FEATURE_END ===== -->
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PaymentSuccess',
  data() {
    return {
      verifying: true,
      paymentVerified: false,
      error: false,
      errorMessage: '',
      subscriptionDetails: null,
      sessionId: null
    };
  },
  mounted() {
    /* ===== SUBSCRIPTION_FEATURE_START ===== */
    // this.verifyPayment();
    /* ===== SUBSCRIPTION_FEATURE_END ===== */

    // Redirect to dashboard immediately (free access)
    this.$router.push('/dashboard');
  },
  methods: {
    async verifyPayment() {
      try {
        // Get session ID from URL params
        const urlParams = new URLSearchParams(window.location.search);
        this.sessionId = urlParams.get('session_id');

        if (!this.sessionId) {
          // Try to get from localStorage as backup
          this.sessionId = localStorage.getItem('stripe_session_id');
        }

        if (!this.sessionId) {
          throw new Error('No payment session found');
        }

        // Get token from store or localStorage (fallback)
        let token = (this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']);
        
        // Try to recover from localStorage if not in store
        if (!token) {
          token = localStorage.getItem('auth_token');
          
          // If we found a token in localStorage, restore it to the store
          if (token) {
            // Fetch user profile first to get complete user data
            try {
              const profileResponse = await axios.get(
                `${process.env.VUE_APP_API_ENDPOINT}/api/users/profile`,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                }
              );
              
              // Restore auth state with token and user data
              this.$store.commit('auth/login', {
                token: token,
                user: profileResponse.data
              });
            } catch (profileError) {
              console.error('Error fetching profile:', profileError);
              // If profile fetch fails, just restore the token
              this.$store.commit('auth/login', {
                token: token,
                user: null
              });
            }
          }
        }

        if (!token) {
          // No token found anywhere, redirect to login
          this.$router.push('/login');
          throw new Error('No authentication token found');
        }

        // Verify the payment with backend
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/verify-session/${this.sessionId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.data.success && response.data.paid) {
          this.paymentVerified = true;
          this.subscriptionDetails = response.data.subscription;
          
          // Track successful payment
          this.$posthog.capture('payment_successful', {
            plan_type: response.data.subscription ? response.data.subscription.plan_type : 'unknown',
            subscription_status: response.data.subscription ? response.data.subscription.status : 'unknown'
          });
          
          // Clear stored session data
          localStorage.removeItem('stripe_session_id');
          
          // Fetch user data to update store
          await this.fetchUserData();
        } else {
          throw new Error('Payment verification failed');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        this.error = true;
        this.errorMessage = (error.response && error.response.data && error.response.data.error) || error.message || 'Failed to verify payment. Please contact support.';
      } finally {
        this.verifying = false;
      }
    },

    async fetchUserData() {
      try {
        const token = (this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']) || localStorage.getItem('auth_token');
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/profile`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.data) {
          // Update user in store (profile endpoint returns user data directly)
          this.$store.commit('auth/setUser', response.data);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    },

    goToDashboard() {
      this.$router.push('/dashboard');
    },

    async downloadInvoice() {
      try {
        // Get latest invoice
        const token = (this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']) || localStorage.getItem('user_token');
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/invoices`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            params: {
              limit: 1
            }
          }
        );

        if (response.data.invoices && response.data.invoices.length > 0) {
          const invoice = response.data.invoices[0];
          
          // Get download URL
          const downloadResponse = await axios.get(
            `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/invoice/${invoice.id}/download`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (downloadResponse.data.url) {
            window.open(downloadResponse.data.url, '_blank');
          }
        }
      } catch (error) {
        console.error('Error downloading invoice:', error);
        this.$swal({
          icon: 'error',
          title: 'Download Failed',
          text: 'Unable to download invoice. Please try again later.'
        });
      }
    },

    retryPayment() {
      // Go back to registration or subscription page
      this.$router.push('/register');
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.payment-success-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.payment-success-container > div {
  background: transparent;
}

.payment-success-container .max-w-md {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
</style>